import React, { useEffect, useState, createContext, useContext, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";

// Pages
import LoginPage from "@/pages/LoginPage";
import DashboardPage from "@/pages/DashboardPage";
import WebsiteTrafficPage from "@/pages/WebsiteTrafficPage";
import SocialInsightsPage from "@/pages/SocialInsightsPage";
import FormSubmissionsPage from "@/pages/FormSubmissionsPage";
import MQLsPage from "@/pages/MQLsPage";
import SQLsPage from "@/pages/SQLsPage";
import DealsPage from "@/pages/DealsPage";
import GoogleAdsPage from "@/pages/GoogleAdsPage";
import ForHeathPage from "@/pages/ForHeathPage";
import ClientsPage from "@/pages/ClientsPage";
import AdminPanelPage from "@/pages/AdminPanelPage";
import SuperAdminPage from "@/pages/SuperAdminPage";
import SuperAdminPanelPage from "@/pages/SuperAdminPanelPage";
import SharedReportPage from "@/pages/SharedReportPage";
import LinkedInPageSelectPage from "@/pages/LinkedInPageSelectPage";
import LinkedInAdsSelectPage from "@/pages/LinkedInAdsSelectPage";
import GoogleAccountSelectPage from "@/pages/GoogleAccountSelectPage";
import EmergencyAccessPage from "@/pages/EmergencyAccessPage";
import LeadScoringPage from "@/pages/LeadScoringPage";
import AIChatPage from "@/pages/AIChatPage";
import AnomalyDetectionPage from "@/pages/AnomalyDetectionPage";
import GoalsPage from "@/pages/GoalsPage";
import AnnouncementsPage from "@/pages/AnnouncementsPage";
import GSCTrackingPage from "@/pages/GSCTrackingPage";
import GSCSiteSelectPage from "@/pages/GSCSiteSelectPage";
import ChartExportPage from "@/pages/ChartExportPage";
import ReportsPage from "@/pages/ReportsPage";
import UnifiedReportingPage from "@/pages/UnifiedReportingPage";
import LinkedInABMAgentPage from "@/pages/LinkedInABMAgentPage";
import GoogleAdsApiPage from "@/pages/GoogleAdsApiPage";

// Demo Pages
import DemoWebsiteTrafficPage from "@/pages/demo/DemoWebsiteTrafficPage";
import DemoSocialInsightsPage from "@/pages/demo/DemoSocialInsightsPage";
import { DemoPageWrapper } from "@/components/DemoPageWrapper";

// Layout
import SidebarLayout from "@/components/layout/SidebarLayout";

// Contexts
import { DateFilterProvider } from "@/contexts/DateFilterContext";
import { BrandingProvider } from "@/contexts/BrandingContext";
import { PresentationModeProvider, usePresentationMode } from "@/contexts/PresentationModeContext";
import { FeatureFlagsProvider } from "@/contexts/FeatureFlagsContext";

// Components
import DataProtectionModal from "@/components/DataProtectionModal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Helper to check if currently impersonating
export const isImpersonating = () => {
  return !!localStorage.getItem("impersonation_token");
};

// Helper to get impersonation details
export const getImpersonationDetails = () => {
  if (!isImpersonating()) return null;
  return {
    targetEmail: localStorage.getItem("impersonation_target_email"),
    targetName: localStorage.getItem("impersonation_target_name"),
    tenantId: localStorage.getItem("impersonation_tenant_id"),
    tenantName: localStorage.getItem("impersonation_tenant_name"),
    originalToken: localStorage.getItem("impersonation_original_token")
  };
};

// End impersonation and return to admin session
export const endImpersonation = async () => {
  const impersonationToken = localStorage.getItem("impersonation_token");
  const originalToken = localStorage.getItem("impersonation_original_token");
  
  if (!impersonationToken || !originalToken) {
    console.error("No impersonation session found");
    return;
  }
  
  try {
    await axios.post(`${API}/auth/end-impersonation`, {
      impersonation_token: impersonationToken
    });
  } catch (error) {
    console.error("Error ending impersonation:", error);
  }
  
  // Clean up impersonation data
  localStorage.removeItem("impersonation_token");
  localStorage.removeItem("impersonation_original_token");
  localStorage.removeItem("impersonation_target_email");
  localStorage.removeItem("impersonation_target_name");
  localStorage.removeItem("impersonation_tenant_id");
  localStorage.removeItem("impersonation_tenant_name");
  
  // Restore original admin token
  localStorage.setItem("session_token", originalToken);
  
  // Full page reload to reset state
  window.location.href = "/super-admin";
};

// Impersonation Banner Component
const ImpersonationBanner = () => {
  const details = getImpersonationDetails();
  const [previewMode, setPreviewMode] = React.useState(() => {
    return sessionStorage.getItem("feature_preview_mode") === "true";
  });
  
  const togglePreviewMode = () => {
    const newValue = !previewMode;
    setPreviewMode(newValue);
    if (newValue) {
      sessionStorage.setItem("feature_preview_mode", "true");
    } else {
      sessionStorage.removeItem("feature_preview_mode");
    }
    // Trigger a re-render of components using feature flags
    window.dispatchEvent(new Event("feature-preview-changed"));
  };
  
  if (!details) return null;
  
  return (
    <div className="fixed top-0 left-0 right-0 z-[9999] bg-gradient-to-r from-purple-600 to-indigo-600 text-white px-4 py-2 flex items-center justify-between shadow-lg">
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-2 bg-white/20 rounded-full px-3 py-1">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
          <span className="font-medium text-sm">Impersonating</span>
        </div>
        <div>
          <span className="font-semibold">{details.targetName || details.targetEmail}</span>
          {details.tenantName && (
            <span className="text-purple-200 ml-2">({details.tenantName})</span>
          )}
        </div>
      </div>
      <div className="flex items-center gap-3">
        {/* Preview Features Toggle */}
        <button
          onClick={togglePreviewMode}
          className={`flex items-center gap-2 px-3 py-1.5 rounded-lg font-medium text-sm transition-colors ${
            previewMode 
              ? 'bg-amber-400 text-amber-900 hover:bg-amber-300' 
              : 'bg-white/20 text-white hover:bg-white/30'
          }`}
          title="Preview all features (for testing new features without affecting the user)"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
          </svg>
          {previewMode ? 'Preview ON' : 'Preview Features'}
        </button>
        
        <button
          onClick={endImpersonation}
          className="flex items-center gap-2 bg-white text-purple-700 hover:bg-purple-100 px-4 py-1.5 rounded-lg font-medium text-sm transition-colors"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
          </svg>
          Exit
        </button>
      </div>
    </div>
  );
};

// Helper to get current viewing tenant ID (for super admin impersonation)
export const getViewingTenantId = () => {
  return sessionStorage.getItem("viewing_tenant_id");
};

// Axios interceptor to add tenant context to all API requests
axios.interceptors.request.use((config) => {
  const viewingTenantId = getViewingTenantId();
  if (viewingTenantId) {
    // Add X-Tenant-ID to BOTH params and headers for all requests
    // Some endpoints use Query params, others use Headers (especially file uploads)
    config.params = {
      ...config.params,
      "X-Tenant-ID": viewingTenantId
    };
    config.headers = {
      ...config.headers,
      "X-Tenant-ID": viewingTenantId
    };
  }
  return config;
});

// Auth Context
export const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

// Auth Provider Component
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionToken, setSessionToken] = useState(() => {
    return localStorage.getItem("session_token");
  });

  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem("session_token");
      if (token) {
        try {
          const response = await axios.get(`${API}/auth/me?Authorization=Bearer ${token}`);
          setUser(response.data);
          setSessionToken(token);
          
          // ALWAYS fetch the correct tenant for this user on every auth check
          // This prevents stale tenant data from persisting across logins
          const userEmail = response.data?.email?.toLowerCase();
          
          // Check if user changed - if so, clear stale tenant context
          const previousUser = sessionStorage.getItem("tenant_for_user");
          if (previousUser && previousUser !== userEmail) {
            console.log(`User changed from ${previousUser} to ${userEmail} - clearing tenant context`);
            sessionStorage.removeItem("viewing_tenant_id");
            sessionStorage.removeItem("viewing_tenant_name");
            sessionStorage.removeItem("tenant_for_user");
          }
          
          try {
            const homeResponse = await axios.get(`${API}/tenants/default-home`, {
              params: { Authorization: `Bearer ${token}` }
            });
            
            const correctTenantId = homeResponse.data?.tenant_id;
            const currentTenantId = sessionStorage.getItem("viewing_tenant_id");
            
            // Always update if tenant is wrong or missing
            if (correctTenantId && currentTenantId !== correctTenantId) {
              console.log(`Tenant mismatch! Current: ${currentTenantId}, Correct: ${correctTenantId} for user ${userEmail}`);
              sessionStorage.setItem("viewing_tenant_id", correctTenantId);
              sessionStorage.setItem("viewing_tenant_name", homeResponse.data.name || "");
              sessionStorage.setItem("tenant_for_user", userEmail);
              console.log("Updated tenant context to:", homeResponse.data.name, correctTenantId);
              window.dispatchEvent(new Event("tenant-changed"));
            } else if (correctTenantId) {
              // Tenant is correct, just ensure user tracking is set
              sessionStorage.setItem("tenant_for_user", userEmail);
              console.log("Tenant context verified:", homeResponse.data.name, correctTenantId);
            } else {
              console.log("No default tenant found for user");
              sessionStorage.removeItem("viewing_tenant_id");
              sessionStorage.removeItem("viewing_tenant_name");
              sessionStorage.removeItem("tenant_for_user");
              window.dispatchEvent(new Event("tenant-changed"));
            }
          } catch (tenantError) {
            console.error("Failed to fetch default tenant:", tenantError);
            // Don't clear on error - keep existing tenant to avoid breaking the UI
          }
        } catch (error) {
          console.error("Auth check failed:", error);
          localStorage.removeItem("session_token");
          // Clear ALL session data on auth failure
          sessionStorage.clear();
          setUser(null);
          setSessionToken(null);
        }
      } else {
        // No token - clear everything
        sessionStorage.clear();
      }
      setLoading(false);
    };

    checkAuth();
  }, []);

  const login = async (sessionId) => {
    try {
      const response = await axios.post(`${API}/auth/session`, { session_id: sessionId });
      const { session_token, user: userData } = response.data;
      
      // CRITICAL: Clear any stale tenant context from previous sessions
      // This ensures tenant isolation when switching between users
      sessionStorage.removeItem("viewing_tenant_id");
      sessionStorage.removeItem("viewing_tenant_name");
      sessionStorage.removeItem("tenant_for_user");
      
      localStorage.setItem("session_token", session_token);
      setSessionToken(session_token);
      setUser(userData);
      // Dispatch event to trigger branding refresh
      window.dispatchEvent(new CustomEvent("auth-changed"));
      toast.success(`Welcome, ${userData.name}!`);
      return userData;
    } catch (error) {
      console.error("Login failed:", error);
      toast.error("Login failed. Please try again.");
      throw error;
    }
  };

  const loginWithMicrosoft = async (code) => {
    try {
      const response = await axios.post(`${API}/auth/microsoft/callback`, { code });
      const { session_token, user: userData } = response.data;
      
      // CRITICAL: Clear any stale tenant context from previous sessions
      sessionStorage.removeItem("viewing_tenant_id");
      sessionStorage.removeItem("viewing_tenant_name");
      sessionStorage.removeItem("tenant_for_user");
      
      localStorage.setItem("session_token", session_token);
      setSessionToken(session_token);
      setUser(userData);
      // Dispatch event to trigger branding refresh
      window.dispatchEvent(new CustomEvent("auth-changed"));
      toast.success(`Welcome, ${userData.name}!`);
      return userData;
    } catch (error) {
      console.error("Microsoft login failed:", error);
      toast.error("Microsoft login failed. Please try again.");
      throw error;
    }
  };

  const logout = async () => {
    try {
      if (sessionToken) {
        await axios.post(`${API}/auth/logout?Authorization=Bearer ${sessionToken}`);
      }
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      // Clear EVERYTHING on logout
      localStorage.removeItem("session_token");
      localStorage.removeItem("presentationMode");
      sessionStorage.clear(); // Nuclear option - clear ALL sessionStorage
      setSessionToken(null);
      setUser(null);
      toast.success("Logged out successfully");
    }
  };

  // Super admin emails (JCI master admins) - MUST match backend config.py
  const SUPER_ADMIN_EMAILS = ["jason@jci-marketing.com", "kevin@jci-marketing.com"];
  const isSuperAdmin = user?.email && SUPER_ADMIN_EMAILS.map(e => e.toLowerCase()).includes(user.email.toLowerCase());

  // Helper to determine if user is admin (checks both top-level role and tenant membership role)
  const checkIsAdmin = () => {
    // Top-level role check
    if (user?.role === "admin" || user?.role === "master_admin") return true;
    
    // Check tenant_memberships for admin role
    const memberships = user?.tenant_memberships || [];
    const currentTenantId = sessionStorage.getItem("viewing_tenant_id");
    
    // If user has any admin membership, they're an admin
    for (const membership of memberships) {
      const membershipRole = typeof membership === 'object' ? membership.role : null;
      const membershipTenantId = typeof membership === 'object' ? membership.tenant_id : membership;
      
      // If viewing a specific tenant, check that tenant's membership
      if (currentTenantId && membershipTenantId === currentTenantId) {
        if (membershipRole === "admin" || membershipRole === "editor") return true;
      }
      // Also check if any membership has admin role
      if (membershipRole === "admin" || membershipRole === "editor") return true;
    }
    
    return false;
  };

  const value = {
    user,
    sessionToken,
    loading,
    login,
    loginWithMicrosoft,
    logout,
    isAuthenticated: !!user,
    isMasterAdmin: isSuperAdmin, // Master admin = super admin (checked against SUPER_ADMIN_EMAILS)
    isAdmin: isSuperAdmin || checkIsAdmin(),
    isEditor: user?.role === "editor" || user?.role === "admin" || user?.role === "master_admin" || checkIsAdmin(),
    isViewer: user?.role === "viewer",
    isSuperAdmin,
    // Permissions from backend
    permissions: user?.permissions || {
      can_view: true,
      can_edit: false,
      can_trigger_actions: false,
      can_access_admin: false,
      can_manage_integrations: false,
      can_manage_users: false,
      can_see_notes: false,
      can_change_dates: false,
      is_jci_user: false,
      is_master_admin: false,
      is_viewer: true
    }
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Protected Route Component
const ProtectedRoute = ({ children, adminOnly = false, masterAdminOnly = false }) => {
  const { isAuthenticated, loading, isAdmin, isMasterAdmin } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#00A5A8] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (masterAdminOnly && !isMasterAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  if (adminOnly && !isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Auth Callback Handler
const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    
    const handleCallback = async () => {
      // Check for token in query params (from Google OAuth via backend)
      const queryParams = new URLSearchParams(window.location.search);
      const googleToken = queryParams.get("token");
      
      // Check for session_id in hash (Emergent Auth)
      const hash = window.location.hash;
      const hashParams = new URLSearchParams(hash.replace("#", ""));
      const sessionId = hashParams.get("session_id");
      const sessionToken = hashParams.get("session_token");
      
      console.log("AuthCallback processing:", { googleToken: !!googleToken, sessionId: !!sessionId, sessionToken: !!sessionToken, hash });

      if (googleToken) {
        // Google OAuth - token from YOUR Google Sign-In
        try {
          localStorage.setItem("session_token", googleToken);
          window.dispatchEvent(new CustomEvent("auth-changed"));
          await axios.get(`${API}/auth/me?Authorization=Bearer ${googleToken}`);
          window.history.replaceState(null, "", window.location.pathname);
          window.location.href = "/app/dashboard";
        } catch (error) {
          console.error("Google session validation failed:", error);
          navigate("/login?error=session_invalid", { replace: true });
        }
      } else if (sessionId) {
        // Emergent Auth - verify with Emergent Auth
        console.log("Processing Emergent Auth session_id:", sessionId);
        try {
          const userData = await login(sessionId);
          console.log("Login successful:", userData);
          // Clear the hash from URL
          window.history.replaceState(null, "", window.location.pathname);
          navigate("/app/dashboard", { replace: true });
        } catch (error) {
          console.error("Emergent Auth login failed:", error);
          navigate("/login?error=auth_failed", { replace: true });
        }
      } else if (sessionToken) {
        // Microsoft OAuth - token already created by backend
        try {
          localStorage.setItem("session_token", sessionToken);
          window.dispatchEvent(new CustomEvent("auth-changed"));
          await axios.get(`${API}/auth/me?Authorization=Bearer ${sessionToken}`);
          window.history.replaceState(null, "", window.location.pathname);
          window.location.href = "/app/dashboard";
        } catch (error) {
          console.error("Session validation failed:", error);
          navigate("/login", { replace: true });
        }
      } else {
        console.log("No auth token found, redirecting to login");
        navigate("/login", { replace: true });
      }
    };

    handleCallback();
  }, [login, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="text-center">
        <div className="w-12 h-12 border-4 border-[#00A5A8] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-slate-600">Authenticating...</p>
      </div>
    </div>
  );
};

// Microsoft Auth Callback Handler
const MicrosoftAuthCallback = () => {
  const navigate = useNavigate();
  const { loginWithMicrosoft } = useAuth();

  useEffect(() => {
    const handleCallback = async () => {
      const params = new URLSearchParams(window.location.search);
      const code = params.get("code");
      const error = params.get("error");

      if (error) {
        console.error("Microsoft OAuth error:", error);
        navigate("/login", { replace: true });
        return;
      }

      if (code) {
        try {
          await loginWithMicrosoft(code);
          navigate("/app/dashboard", { replace: true });
        } catch (error) {
          console.error("Microsoft login failed:", error);
          navigate("/login", { replace: true });
        }
      } else {
        navigate("/login", { replace: true });
      }
    };

    handleCallback();
  }, [loginWithMicrosoft, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="text-center">
        <div className="w-12 h-12 border-4 border-[#00A5A8] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-slate-600">Signing in with Microsoft...</p>
      </div>
    </div>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <PresentationModeProvider>
          <BrandingProvider>
            <FeatureFlagsProvider>
              <DateFilterProvider>
                <Toaster position="top-right" richColors />
                <AppWithDataProtection />
              </DateFilterProvider>
            </FeatureFlagsProvider>
          </BrandingProvider>
        </PresentationModeProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

// Separate component to use hooks within providers
function AppWithDataProtection() {
  const { user, isAuthenticated, loading } = useAuth();
  const { isPresentationMode, setIsPresentationMode } = usePresentationMode();
  const location = useLocation();
  
  // Always show modal on fresh login - no persistence, always starts masked
  const [dataProtectionConfirmed, setDataProtectionConfirmed] = useState(false);
  const [prevAuthenticated, setPrevAuthenticated] = useState(isAuthenticated);

  // CRITICAL: Check for session_id in URL hash SYNCHRONOUSLY during render
  // This prevents race conditions - must happen BEFORE ProtectedRoute checks
  const hash = window.location.hash;
  if (hash && hash.includes('session_id=')) {
    // Redirect to AuthCallback to process the session_id
    return <AuthCallback />;
  }

  // Reset confirmation when user logs out (detect auth state change)
  if (prevAuthenticated && !isAuthenticated) {
    setDataProtectionConfirmed(false);
    setPrevAuthenticated(false);
  } else if (!prevAuthenticated && isAuthenticated) {
    setPrevAuthenticated(true);
  }

  // Derive showDataProtection from state (no useEffect needed)
  const isProtectedRoute = location.pathname.startsWith('/app/') || location.pathname === '/';
  const isPublicRoute = ['/login', '/auth/callback', '/auth/microsoft/callback', '/shared', '/secure-admin-', '/jci-internal-'].some(
    route => location.pathname.startsWith(route)
  );
  const showDataProtection = isAuthenticated && !loading && !dataProtectionConfirmed && isProtectedRoute && !isPublicRoute;

  const handleConfirmRealData = () => {
    setIsPresentationMode(false);
    setDataProtectionConfirmed(true);
    // No sessionStorage - choice only lasts for this session
  };

  const handlePresentationMode = () => {
    setIsPresentationMode(true);
    setDataProtectionConfirmed(true);
    // No sessionStorage - choice only lasts for this session
  };

  return (
    <>
      <ImpersonationBanner />
      <DataProtectionModal
        isOpen={showDataProtection}
        onConfirm={handleConfirmRealData}
        onPresentationMode={handlePresentationMode}
        userName={user?.name?.split(' ')[0] || 'User'}
      />
      <Routes>
        {/* Public Routes */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/auth/microsoft/callback" element={<MicrosoftAuthCallback />} />
        <Route path="/auth/linkedin/select-page" element={<LinkedInPageSelectPage />} />
        <Route path="/linkedin-org-select" element={<LinkedInPageSelectPage />} />
        <Route path="/linkedin-ads-select" element={<LinkedInAdsSelectPage />} />
        <Route path="/google-account-select" element={<GoogleAccountSelectPage />} />
        <Route path="/gsc-site-select" element={<GSCSiteSelectPage />} />
        <Route path="/dashboard" element={<AuthCallback />} />
        <Route path="/shared/:token" element={<SharedReportPage />} />
        <Route path="/chart-export" element={<ChartExportPage />} />
        
        {/* Secret Emergency Access - MUST be before catch-all, completely outside auth */}
        <Route path="/jci-internal-7f8a9b2c4d6e1f3a5b7c9d0e2f4a6b8c" element={<EmergencyAccessPage />} />
        <Route path="/secure-admin-x7k9m2p4q8r1s5t3w6y0z8a1b3c5d7e9f2g4h6j8k0l2n4p6q8r0s2t4v6x8y0z1a3b5c7d9e1f3g5h7j9k1l3m5n7p9q1r3s5t7v9w1x3y5z7" element={<EmergencyAccessPage />} />

        {/* Protected Routes with Sidebar Layout */}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <SidebarLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="/app/dashboard" replace />} />
          <Route path="app/dashboard" element={<DashboardPage />} />
          <Route path="app/website-traffic" element={
            <DemoPageWrapper 
              DemoComponent={DemoWebsiteTrafficPage} 
              RealComponent={WebsiteTrafficPage} 
            />
          } />
                <Route path="app/social-insights" element={
                  <DemoPageWrapper 
                    DemoComponent={DemoSocialInsightsPage} 
                    RealComponent={SocialInsightsPage} 
                  />
                } />
                <Route path="app/form-submissions" element={<FormSubmissionsPage />} />
                <Route path="app/mqls" element={<MQLsPage />} />
                <Route path="app/sqls" element={<SQLsPage />} />
                <Route path="app/deals" element={<DealsPage />} />
                <Route path="app/google-ads" element={<GoogleAdsPage />} />
                <Route path="app/for-marketing-insights" element={<ForHeathPage />} />
                <Route path="app/lead-scoring" element={<LeadScoringPage />} />
                <Route path="app/ai-chat" element={<AIChatPage />} />
                <Route path="app/anomaly-detection" element={<AnomalyDetectionPage />} />
                <Route path="app/goals" element={<GoalsPage />} />
                <Route path="app/goals/gsc-tracking" element={<GSCTrackingPage />} />
                <Route path="app/announcements" element={<AnnouncementsPage />} />
                <Route path="app/reports" element={<ReportsPage />} />
                <Route path="app/unified-reporting" element={<UnifiedReportingPage />} />
                <Route path="app/linkedin-abm-agent" element={<LinkedInABMAgentPage />} />
                <Route path="app/google-ads-api" element={<GoogleAdsApiPage />} />
                <Route
                  path="app/admin"
                  element={
                    <ProtectedRoute adminOnly>
                      <AdminPanelPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="app/super-admin"
                  element={
                    <ProtectedRoute masterAdminOnly>
                      <SuperAdminPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="app/admin-panel"
                  element={
                    <ProtectedRoute masterAdminOnly>
                      <SuperAdminPanelPage />
                    </ProtectedRoute>
                  }
                />
            </Route>

            {/* Catch all */}
            <Route path="*" element={<Navigate to="/login" replace />} />
          </Routes>
        </>
      );
}

export default App;
